import { daysBetween, type Day } from "./day.js";

/** What the big «Üben» button needs to know about a list. */
export interface PickableList {
  item_count: number;
  due_date: string | null;
  /** The child's own mark (issue #99). Archived lists never reach Start. */
  mark: "current" | "archived" | null;
  current_at: number | null;
}

export type NextStep<L> =
  | { kind: "list"; list: L }
  | { kind: "due"; langs: Array<{ lang: "en" | "fr"; n: number }> }
  | { kind: "none" };

/** «Aktuell» across own and class lists, in the order they were marked (issue #99). */
export function currentLists<L extends PickableList>(mine: readonly L[], fromClass: readonly L[]): L[] {
  return [...mine, ...fromClass]
    .filter((list) => list.mark === "current")
    .sort((a, b) => (a.current_at ?? 0) - (b.current_at ?? 0));
}

/**
 * What the big button practises (docs/UX.md §2, issues #49 and #99): the list
 * with the nearest exam still ahead — the class's lists included, that is
 * where a teacher's weekly words live — then a list marked «Aktuell», then
 * «heute dran», then the list used last. `mine` and `fromClass` come sorted by
 * last use and without archived lists.
 */
export function nextStep<L extends PickableList>(
  mine: readonly L[],
  fromClass: readonly L[],
  due: { en: number; fr: number },
  today: Day,
): NextStep<L> {
  const withExam = [...mine, ...fromClass]
    .filter((list) => list.item_count > 0 && list.due_date !== null && daysBetween(today, list.due_date) >= 0)
    .sort((a, b) => (a.due_date ?? "").localeCompare(b.due_date ?? ""));
  if (withExam[0]) return { kind: "list", list: withExam[0] };

  const current = currentLists(mine, fromClass).find((list) => list.item_count > 0);
  if (current) return { kind: "list", list: current };

  const langs = (["en", "fr"] as const)
    .map((lang) => ({ lang, n: due[lang] }))
    .filter((entry) => entry.n > 0)
    .sort((a, b) => b.n - a.n);
  if (langs.length > 0) return { kind: "due", langs };

  const latest = mine.find((list) => list.item_count > 0) ?? fromClass[0];
  return latest ? { kind: "list", list: latest } : { kind: "none" };
}

/** A round the app keeps on the device to practise without a connection (issue #120). */
export type RoundKey = `due:${"en" | "fr"}` | `list:${string}`;

/**
 * The rounds to keep while there is a connection: what the big button on
 * Start offers, «heute dran» per language or one list. Start offers exactly
 * these offline; the list cards lead to a list page, which needs one.
 */
export function roundsToKeep<L extends PickableList & { id: string }>(next: NextStep<L>): RoundKey[] {
  if (next.kind === "list") return [`list:${next.list.id}`];
  if (next.kind === "due") return next.langs.map(({ lang }) => `due:${lang}` as const);
  return [];
}
